import type React from 'react';
import { useState, useCallback } from 'react';
import { Modal, Button, CopyIcon, DownloadIcon } from '@shadeables/ui';
import styles from './ExportModal.module.css';
import type { ExportData } from '@shadeables/shadermodel';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportData: ExportData | null;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  exportData,
}) => {
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedJs, setCopiedJs] = useState(false);

  const handleCopy = useCallback(async (text: string, type: 'html' | 'js') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'html') {
        setCopiedHtml(true);
        setTimeout(() => setCopiedHtml(false), 2000);
      } else {
        setCopiedJs(true);
        setTimeout(() => setCopiedJs(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }, []);

  const handleDownload = useCallback((content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  if (!exportData) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Shader">
      <div className={styles.exportModal}>
        <div className={styles.codeSection}>
          <div className={styles.codeBlock}>
            <div className={styles.codeHeader}>
              <span>HTML Canvas</span>
              <div className={styles.codeActions}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<CopyIcon />}
                  onClick={() => handleCopy(exportData.html, 'html')}
                >
                  {copiedHtml ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<DownloadIcon />}
                  onClick={() =>
                    handleDownload(exportData.html, 'shader-canvas.html')
                  }
                >
                  Download
                </Button>
              </div>
            </div>
            <div className={styles.codeContent}>
              <pre>
                <code>{exportData.html}</code>
              </pre>
            </div>
          </div>

          <div className={styles.codeBlock}>
            <div className={styles.codeHeader}>
              <span>JavaScript Animation</span>
              <div className={styles.codeActions}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<CopyIcon />}
                  onClick={() => handleCopy(exportData.javascript, 'js')}
                >
                  {copiedJs ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<DownloadIcon />}
                  onClick={() =>
                    handleDownload(exportData.javascript, 'shader-animation.js')
                  }
                >
                  Download
                </Button>
              </div>
            </div>
            <div className={styles.codeContent}>
              <pre>
                <code>{exportData.javascript}</code>
              </pre>
            </div>
          </div>
        </div>

        <div className={styles.instructions}>
          <h4>Usage Instructions:</h4>
          <ol>
            <li>Add the HTML canvas element to your webpage</li>
            <li>
              Include the JavaScript code in a script tag or external file
            </li>
            <li>Make sure to run the JavaScript after the DOM has loaded</li>
            <li>Your shader animation will start automatically!</li>
          </ol>
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
